import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, Printer, FileDown, Settings, MoreVertical, Plus, X } from 'lucide-react';
import { customersApi, invoicesApi, paymentsApi } from '../api';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [customerStats, setCustomerStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  
  // Modal states
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Form states
  const [newCustomerForm, setNewCustomerForm] = useState({
    name: '',
    company_name: '',
    email: '',
    phone: ''
  });
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'Cash',
    notes: ''
  });
  const [invoiceForm, setInvoiceForm] = useState({
    description: '',
    quantity: 1,
    unit_price: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [customersRes, statsRes] = await Promise.all([
        customersApi.getAll(),
        customersApi.getStats()
      ]);
      setCustomers(customersRes.data);
      setCustomerStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (customer.company_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (sortField === 'open_balance') {
      return sortDirection === 'asc' ? (aVal || 0) - (bVal || 0) : (bVal || 0) - (aVal || 0);
    }
    return sortDirection === 'asc'
      ? String(aVal || '').localeCompare(String(bVal || ''))
      : String(bVal || '').localeCompare(String(aVal || ''));
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleSelectAll = () => {
    if (selectedCustomers.length === sortedCustomers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(sortedCustomers.map(c => c.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedCustomers(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleCreateCustomer = async () => {
    try {
      await customersApi.create(newCustomerForm);
      setShowNewCustomer(false);
      setNewCustomerForm({ name: '', company_name: '', email: '', phone: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to create customer:', error);
    }
  };

  const handleReceivePayment = async () => {
    try {
      await paymentsApi.create({
        customer_id: selectedCustomer.id,
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes
      });
      setShowPaymentModal(false);
      setPaymentForm({ amount: '', payment_method: 'Cash', notes: '' });
      setSelectedCustomer(null);
      fetchData();
    } catch (error) {
      console.error('Failed to receive payment:', error);
    }
  };

  const handleCreateInvoice = async () => {
    try {
      await invoicesApi.create({
        customer_id: selectedCustomer.id,
        line_items: [{
          description: invoiceForm.description,
          quantity: parseFloat(invoiceForm.quantity),
          unit_price: parseFloat(invoiceForm.unit_price)
        }]
      });
      setShowInvoiceModal(false);
      setInvoiceForm({ description: '', quantity: 1, unit_price: '' });
      setSelectedCustomer(null);
      fetchData();
    } catch (error) {
      console.error('Failed to create invoice:', error);
    }
  };

  const openPaymentModal = (customer) => {
    setSelectedCustomer(customer);
    setPaymentForm({ ...paymentForm, amount: customer.open_balance?.toString() || '' });
    setShowPaymentModal(true);
  };

  const openInvoiceModal = (customer) => {
    setSelectedCustomer(customer);
    setShowInvoiceModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Calculate totals for progress bar
  const progressSegments = [
    { color: '#0077c5', width: '25%' },
    { color: '#0077c5', width: '25%' },
    { color: '#f59e0b', width: '25%' },
    { color: '#e5e7eb', width: '15%' },
    { color: '#2ca01c', width: '10%' }
  ];

  return (
    <div className="flex-1 bg-white overflow-auto">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-medium text-gray-800">Customers</h1>
            <button className="text-[#2ca01c] text-sm hover:underline">
              Check out the new view with filters
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50">
              Customer types
            </button>
            <button 
              className="px-4 py-2 bg-[#2ca01c] text-white rounded text-sm hover:bg-[#249017] flex items-center gap-1"
              onClick={() => setShowNewCustomer(true)}
            >
              New customer <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        {customerStats && (
          <>
            <div className="grid grid-cols-5 gap-6 mb-4">
              <div>
                <p className="text-3xl font-semibold text-gray-900">{formatCurrency(customerStats.estimates)}</p>
                <p className="text-sm text-gray-500">estimates</p>
              </div>
              <div>
                <p className="text-3xl font-semibold text-gray-900">{formatCurrency(customerStats.unbilled_income)}</p>
                <p className="text-sm text-gray-500">Unbilled income</p>
              </div>
              <div>
                <p className="text-3xl font-semibold text-gray-900">{formatCurrency(customerStats.overdue_invoices)}</p>
                <p className="text-sm text-gray-500">overdue invoices</p>
              </div>
              <div>
                <p className="text-3xl font-semibold text-gray-900">{formatCurrency(customerStats.open_invoices)}</p>
                <p className="text-sm text-gray-500">{customerStats.open_invoices_count} open invoices and credits</p>
              </div>
              <div>
                <p className="text-3xl font-semibold text-gray-900">{formatCurrency(customerStats.recently_paid)}</p>
                <p className="text-sm text-gray-500">recently paid</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="flex h-3 rounded-full overflow-hidden mb-8">
              {progressSegments.map((segment, idx) => (
                <div
                  key={idx}
                  className="h-full"
                  style={{ backgroundColor: segment.color, width: segment.width }}
                />
              ))}
            </div>
          </>
        )}

        {/* Search and Actions */}
        <div className="flex items-center justify-between mb-4">
          <div className="relative w-80">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#2ca01c] focus:border-transparent"
            />
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded">
              <Printer className="w-5 h-5" />
            </button>
            <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded">
              <FileDown className="w-5 h-5" />
            </button>
            <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded">
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2ca01c]"></div>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <Checkbox
                      checked={selectedCustomers.length === sortedCustomers.length && sortedCustomers.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                  </th>
                  <th
                    className="text-left px-4 py-3 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      NAME
                      {sortField === 'name' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th
                    className="text-left px-4 py-3 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('company_name')}
                  >
                    <div className="flex items-center gap-1">
                      COMPANY NAME
                      {sortField === 'company_name' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">PHONE</th>
                  <th
                    className="text-left px-4 py-3 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('open_balance')}
                  >
                    <div className="flex items-center gap-1">
                      OPEN BALANCE
                      {sortField === 'open_balance' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {sortedCustomers.map((customer) => (
                  <tr
                    key={customer.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <Checkbox
                        checked={selectedCustomers.includes(customer.id)}
                        onCheckedChange={() => toggleSelect(customer.id)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-900 font-medium">{customer.name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{customer.company_name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{customer.phone}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-900">{formatCurrency(customer.open_balance)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {customer.open_balance > 0 ? (
                          <button 
                            className="text-sm text-[#2ca01c] hover:underline"
                            onClick={() => openPaymentModal(customer)}
                          >
                            Receive payment
                          </button>
                        ) : (
                          <button 
                            className="text-sm text-[#2ca01c] hover:underline"
                            onClick={() => openInvoiceModal(customer)}
                          >
                            Create invoice
                          </button>
                        )}
                        <button className="p-1 text-gray-400 hover:text-gray-600">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* New Customer Modal */}
      <Dialog open={showNewCustomer} onOpenChange={setShowNewCustomer}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>New Customer</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={newCustomerForm.name}
                onChange={(e) => setNewCustomerForm({ ...newCustomerForm, name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="company">Company Name</Label>
              <Input
                id="company"
                value={newCustomerForm.company_name}
                onChange={(e) => setNewCustomerForm({ ...newCustomerForm, company_name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={newCustomerForm.email}
                onChange={(e) => setNewCustomerForm({ ...newCustomerForm, email: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={newCustomerForm.phone}
                onChange={(e) => setNewCustomerForm({ ...newCustomerForm, phone: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCustomer(false)}>Cancel</Button>
            <Button className="bg-[#2ca01c] hover:bg-[#249017]" onClick={handleCreateCustomer}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Receive Payment from {selectedCustomer?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="amount">Amount *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="method">Payment Method</Label>
              <select
                id="method"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={paymentForm.payment_method}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
              >
                <option value="Cash">Cash</option>
                <option value="Check">Check</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Bank Transfer">Bank Transfer</option>
              </select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>Cancel</Button>
            <Button className="bg-[#2ca01c] hover:bg-[#249017]" onClick={handleReceivePayment}>Receive Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Invoice Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create Invoice for {selectedCustomer?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="description">Description *</Label>
              <Input
                id="description"
                value={invoiceForm.description}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, description: e.target.value })}
                placeholder="Services rendered"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="quantity">Quantity</Label>
                <Input
                  id="quantity"
                  type="number"
                  value={invoiceForm.quantity}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, quantity: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="price">Unit Price *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={invoiceForm.unit_price}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, unit_price: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceModal(false)}>Cancel</Button>
            <Button className="bg-[#2ca01c] hover:bg-[#249017]" onClick={handleCreateInvoice}>Create Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
